import React, { Component, useState } from 'react';
import {
  CheckCircle,
  AlertCircle,
  Mail,
  MessageSquare,
  Lock,
  ShieldCheck,
  Briefcase
} from 'lucide-react';

const FORMS = {
  general: {
    id: '262687147970066',
    title: 'Contact form NgandjuiSolutions',
    heading: 'Get in Touch',
    description: 'Have a question or want to work together? Drop me a message!',
    height: 1900
  },
  business: {
    id: '262687940406061',
    title: 'Business Inquiry — Ngandjui Solutions',
    heading: 'Business Inquiry',
    description: 'Tell me about your company, project, and timeline. I will follow up on your business request.',
    height: 1100
  }
};

class ContactErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">
            The contact form could not be displayed. Please refresh the page or email ngandjuialphonse11@gmail.com.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const ContactFormTabs = () => {
  const [activeForm, setActiveForm] = useState('general');
  const [formStatus, setFormStatus] = useState('ready');
  const [isHovered, setIsHovered] = useState(false);

  const currentForm = FORMS[activeForm];

  const switchForm = (formKey) => {
    if (formKey === activeForm) {
      return;
    }
    setActiveForm(formKey);
    setFormStatus('ready');
  };

  return (
    <div className="w-full max-w-4xl mx-auto animate-fadeIn">
      <div className="mb-8 text-center relative overflow-hidden rounded-lg p-6
        bg-gradient-to-r from-blue-500/10 to-purple-500/10 hover:from-blue-500/20 hover:to-purple-500/20
        transition-all duration-500">
        <div className="absolute inset-0 bg-white/5 backdrop-blur-sm" />
        <div className="relative z-10">
          <div className="flex items-center justify-center mb-4 group">
            {activeForm === 'business' ? (
              <Briefcase className="w-8 h-8 text-blue-500 mr-2 transform transition-transform duration-300
                group-hover:rotate-12 group-hover:scale-110" />
            ) : (
              <Mail className="w-8 h-8 text-blue-500 mr-2 transform transition-transform duration-300
                group-hover:rotate-12 group-hover:scale-110" />
            )}
            <h2 className="text-2xl font-bold bg-clip-text text-transparent
              bg-gradient-to-r from-blue-600 to-purple-600">
              {currentForm.heading}
            </h2>
          </div>
          <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
            {currentForm.description}
          </p>
        </div>
      </div>

      <div className="mb-6 flex justify-center">
        <div className="inline-flex rounded-lg border border-blue-100 dark:border-blue-900 bg-white/80 dark:bg-gray-800/80 p-1">
          <button
            type="button"
            onClick={() => switchForm('general')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeForm === 'general'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-blue-600'
            }`}
          >
            General Contact
          </button>
          <button
            type="button"
            onClick={() => switchForm('business')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeForm === 'business'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-blue-600'
            }`}
          >
            Business Inquiry
          </button>
        </div>
      </div>

      {formStatus === 'error' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
          rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-600 dark:text-red-400">Failed to load the form. Please try again.</p>
          </div>
        </div>
      )}

      {formStatus === 'success' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800
          rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <p className="text-green-600 dark:text-green-400">
              {activeForm === 'business'
                ? 'Business inquiry sent successfully! I will get back to you soon.'
                : "Message sent successfully! I'll get back to you soon."}
            </p>
          </div>
        </div>
      )}

      <div
        className="relative rounded-lg hover:shadow-xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`absolute top-4 right-4 z-10 transition-all duration-300
          ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}>
          <div className="flex items-center bg-white/90 dark:bg-gray-800/90 rounded-full px-3 py-1
            shadow-lg backdrop-blur-sm">
            <Lock className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-xs text-gray-600 dark:text-gray-300">Secure Form</span>
          </div>
        </div>

        <iframe
          key={currentForm.id}
          title={currentForm.title}
          allow="geolocation; microphone; camera; fullscreen"
          src={`https://form.jotform.com/${currentForm.id}`}
          className="w-full border-0 rounded-lg bg-white dark:bg-gray-800"
          style={{ height: `${currentForm.height}px` }}
        />
      </div>

      <div className="mt-6 space-y-4">
        <div className="flex justify-center space-x-8">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <ShieldCheck className="w-4 h-4 mr-2 text-green-500" />
            <span>Secure Data Processing</span>
          </div>
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Lock className="w-4 h-4 mr-2 text-green-500" />
            <span>End-to-End Encryption</span>
          </div>
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <MessageSquare className="w-4 h-4 mr-2 text-green-500" />
            <span>24/7 Support</span>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Your data is securely processed and never shared with third parties.
        </p>
      </div>
    </div>
  );
};

const ContactForm = () => (
  <ContactErrorBoundary>
    <ContactFormTabs />
  </ContactErrorBoundary>
);

export default ContactForm;
